import React, { useMemo } from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Paper } from '@mui/material';
import { useParams } from 'react-router';
import { SxStyle } from '../../../types';
import PushNotificationSelect from './components/push-notification-select';
import PushNotificationTitle from './components/push-notification-title';
import PushNotificationDescription from './components/push-notification-description';
import { SUPPORTED_LEAGUES } from '../../../types/app-config';
import ScenePage from '../../common/scene/scene-page';
import SceneHeader from '../../common/scene/scene-header';
import {
  usePushNotificationConfiguration,
  usePushNotificationConfigurationUpdate,
} from '../../../hooks/pushNotification';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

export default function PushNotificationSettings() {
  const { strings } = useStringsContext();

  const [, page] = location.pathname.split('/');
  const params = useParams<{ abbrv: string }>();
  const selectedAbbrv = params.abbrv || null;
  const isLeague = selectedAbbrv ? selectedAbbrv in SUPPORTED_LEAGUES : false;
  const query = usePushNotificationConfiguration(selectedAbbrv || '');
  const mutation = usePushNotificationConfigurationUpdate(selectedAbbrv || '');

  const breadCrumps = useBreadcrumbs({
    beforeLinks: [],
    afterLinks: [
      {
        to: `/${page}/${selectedAbbrv}`,
        text: strings.pushNotifications.title,
      },
    ],
  });

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'content',
        headerName: strings.pushNotifications.settingsContent,
        width: 160,
      },
      {
        field: 'description',
        headerName: strings.pushNotifications.settingsDescription,
        minWidth: 200,
        flex: 1,
      },
      {
        field: 'title',
        headerName: strings.pushNotifications.settingsPushNotificationTitle,
        maxWidth: 250,
        minWidth: 180,
        renderCell: (params: GridRenderCellParams) => (
          <PushNotificationTitle
            pushNotificationSettings={params.row}
            selectedAbbrv={selectedAbbrv!}
            isLoading={mutation.isPending}
            save={async (defaultTitle) => {
              const newData = {
                newsConfiguration: {
                  defaultTitle,
                },
              };

              const res = await mutation.mutateAsync(newData);
              query.refetch();
              return res;
            }}
          />
        ),
      },
      {
        field: 'message',
        headerName: strings.pushNotifications.settingsPushNotificationMessage,
        maxWidth: 250,
        minWidth: 180,
        // defaultDescription={query.data?.newsConfiguration.defaultDescription || ''}
        renderCell: (params: GridRenderCellParams) => (
          <PushNotificationDescription
            pushNotificationSettings={params.row}
            selectedAbbrv={selectedAbbrv!}
            isLoading={mutation.isPending}
            save={async (defaultDescription) => {
              const newData = {
                newsConfiguration: {
                  defaultDescription,
                },
              };
              const res = await mutation.mutateAsync(newData);
              query.refetch();
              return res;
            }}
          />
        ),
      },
      {
        field: 'automation',
        headerName: strings.pushNotifications.settingsPushNotificationAutomation,
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <>
            {params.row.id === 'news' ? (
              <PushNotificationSelect
                pushNotificationSettings={params.row}
                selectedAbbrv={selectedAbbrv!}
                isLoading={mutation.isPending}
                save={async (autoPush) => {
                  const newData = {
                    newsConfiguration: {
                      autoPush,
                    },
                  };
                  const res = await mutation.mutateAsync(newData);
                  query.refetch();
                  return res;
                }}
              />
            ) : null}
          </>
        ),
      },
    ],
    [isLeague, selectedAbbrv, strings]
  );
  const rowsSent = [
    {
      id: 'articles',
      content: strings.pushNotifications.articlesSettingsTitle,
      description: strings.pushNotifications.articlesSettingsDescription,
      title: 'title',
      message: 'description',
      automation: false,
    },
    {
      id: 'polls',
      content: strings.pushNotifications.pollsSettingsTitle,
      description: strings.pushNotifications.pollsSettingsDescription,
      title: 'title',
      message: 'type',
      automation: false,
    },
    {
      id: 'videos',
      content: strings.pushNotifications.videosSettingsTitle,
      description: strings.pushNotifications.videosSettingsDescription,
      title: 'caption',
      message: '',
      automation: false,
    },
    {
      id: 'matches',
      content: strings.pushNotifications.matchesSettingsTitle,
      description: strings.pushNotifications.matchesSettingsDescription,
      title: 'title',
      message: '',
      automation: false,
    },
    {
      id: 'news',
      content: strings.pushNotifications.newsSettingsTitle,
      description: strings.pushNotifications.newsSettingsDescription,
      title: query.data?.newsConfiguration.defaultTitle || '',
      message: query.data?.newsConfiguration.defaultDescription || '',
      automation: query.data?.newsConfiguration.autoPush || false,
    },
  ];
  return (
    <ScenePage title={strings.pushNotifications.pushNotificationSettings} breadCrumps={breadCrumps}>
      <SceneHeader
        title={strings.pushNotifications.pushNotificationSettings}
        backButton={`/pushdashboard/${selectedAbbrv}`}
      />
      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rows={rowsSent || []}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableVirtualization
          hideFooter
          initialState={{
            sorting: {
              sortModel: [],
            },
          }}
        />
      </Paper>
    </ScenePage>
  );
}
